import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { SearchBar } from "@/components/SearchBar";
import { PropertyCard } from "@/components/PropertyCard";
import { SectionTitle, FeatureCard } from "@/components/Bits";
import { WhatsAppButton } from "@/components/WhatsApp";
import { InstallApp } from "@/components/InstallApp";
import { Button } from "@/components/ui/button";
import { Building2, KeyRound, TrendingUp, ConciergeBell, Home as HomeIcon, Globe2, ArrowRight, ShieldCheck, Users, Award } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1613977257363-707ba9348227?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";
const SERVICE_META = [
  { icon: KeyRound, to: "/acheter" },
  { icon: TrendingUp, to: "/investir" },
  { icon: Building2, to: "/gestion" },
  { icon: ConciergeBell, to: "/conciergerie" },
  { icon: HomeIcon, to: "/location-courte-duree" },
  { icon: Globe2, to: "/diaspora" },
];
const WHY_ICONS = [ShieldCheck, Users, Award];

export default function Home() {
  const { t } = useLang();
  const { data: featured = [] } = useQuery({ queryKey: ["featured"], queryFn: async () => (await api.get("/properties", { params: { featured: true, limit: 6 } })).data });
  const [favIds, setFavIds] = useState([]);
  const loadFavs = async () => { try { const { data } = await api.get("/favorites"); setFavIds(data.map((p) => p.id)); } catch {} };
  useEffect(() => { loadFavs(); }, []);

  const services = t("home.services");
  const trust = t("home.trust");
  const why = t("home.why");

  return (
    <div>
      {/* HERO */}
      <section className="relative flex items-start pt-28 pb-16 lg:min-h-[92vh] lg:items-center">
        <div className="absolute inset-0">
          <img src={HERO} alt="Villa" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A192F] via-[#0A192F]/90 to-[#0A192F]/55" />
          <div className="absolute inset-0 bg-[#0A192F]/25" />
        </div>
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20">
          <div className="max-w-3xl fade-up">
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-white/5 px-4 py-1.5 text-xs font-mono uppercase tracking-widest text-gold-light">
              <Award className="h-3.5 w-3.5" /> {t("home.heroEyebrow")}
            </span>
            <h1 className="mt-6 font-serif text-4xl sm:text-6xl lg:text-7xl font-bold text-white leading-[1.05] text-shadow-lg">
              {t("home.heroTitle1")}<br /><span className="text-gold">{t("home.heroTitleAccent")}</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-white/85 leading-relaxed">{t("home.heroSubtitle")}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/acheter"><Button data-testid="hero-see-properties" className="bg-gold text-navy hover:bg-gold-light font-semibold h-12 px-8 rounded-lg text-base">{t("actions.seeProperties")} <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
              <WhatsAppButton label={t("actions.talkAdvisor")} testid="hero-whatsapp" message="Bonjour IMmOMAXE, je souhaite parler à un conseiller." className="h-12 px-8 text-base" />
            </div>
          </div>
          <div className="mt-14 max-w-5xl">
            <SearchBar />
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {trust.map(([n, l]) => (
            <div key={l}>
              <div className="font-serif text-3xl sm:text-4xl font-bold text-gold">{n}</div>
              <div className="mt-1 text-xs uppercase tracking-widest text-white/60">{l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <SectionTitle eyebrow={t("home.featEyebrow")} title={t("home.featTitle")} subtitle={t("home.featSubtitle")} />
          <Link to="/acheter" className="text-sm font-medium text-gold hover:underline" data-testid="all-properties-link">{t("home.featSeeAll")}</Link>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
          {featured.map((p) => <PropertyCard key={p.id} property={p} favored={favIds.includes(p.id)} onFavChange={loadFavs} />)}
        </div>
      </section>

      {/* Services */}
      <section className="bg-white border-y border-black/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20">
          <SectionTitle center eyebrow={t("home.servEyebrow")} title={t("home.servTitle")} subtitle={t("home.servSubtitle")} />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {SERVICE_META.map((s, i) => (
              <Link key={s.to} to={s.to} data-testid={`service-${s.to.replace(/\//g, "")}`} className="block">
                <FeatureCard icon={s.icon} title={services[i]?.title} desc={services[i]?.desc} />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-20 grid lg:grid-cols-2 gap-14 items-center">
        <div className="relative">
          <img src="https://images.unsplash.com/photo-1670589953882-b94c9cb380f5?w=900&q=85" alt="Résidence" className="rounded-2xl w-full aspect-[4/3] object-cover" />
          <div className="absolute -bottom-6 -right-4 sm:right-6 rounded-xl bg-navy text-white p-5 shadow-2xl max-w-[220px]">
            <div className="font-serif text-2xl text-gold">{t("home.whyBadgeTitle")}</div>
            <p className="mt-1 text-sm text-white/70">{t("home.whyBadgeDesc")}</p>
          </div>
        </div>
        <div>
          <SectionTitle eyebrow={t("home.whyEyebrow")} title={t("home.whyTitle")} />
          <div className="mt-8 space-y-5">
            {why.map((item, i) => {
              const Icon = WHY_ICONS[i];
              return (
                <div key={item.t} className="flex gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gold/15 text-gold"><Icon className="h-5 w-5" /></span>
                  <div><h4 className="font-semibold text-navy">{item.t}</h4><p className="text-sm text-slate-600 mt-1">{item.d}</p></div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Install PWA */}
      <InstallApp />

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-20">
        <div className="relative overflow-hidden rounded-3xl bg-navy px-8 sm:px-16 py-16 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-white">{t("home.ctaTitle")}</h2>
          <p className="mt-3 text-white/70 max-w-xl mx-auto">{t("home.ctaSubtitle")}</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact"><Button className="bg-gold text-navy hover:bg-gold-light font-semibold h-12 px-8 rounded-lg">{t("home.ctaEstimate")}</Button></Link>
            <WhatsAppButton label={t("home.ctaWhatsapp")} variant="outline" className="h-12 px-8 !border-white/40 !text-white hover:!bg-white/10" />
          </div>
        </div>
      </section>
    </div>
  );
}
